import React, { createContext, useContext, useId, type ReactNode, type HTMLAttributes, type InputHTMLAttributes } from 'react';

type InputContextValue = {
  id: string;
  error?: boolean;
  type?: 'round' | 'square';
};

const InputContext = createContext<InputContextValue | null>(null);

function useInputContext() {
  const context = useContext(InputContext);
  if (!context) throw new Error('Input compound components must be used within <Input.Root>.');
  return context;
}

/** Root */
interface InputRootProps extends HTMLAttributes<HTMLDivElement> {
  error?: boolean;
  id?: string;
  type?: 'round' | 'square';
  children: ReactNode;
}

function Root({ error, id, type, className, children, ...props }: InputRootProps) {
  const autoId = useId();
  const inputId = id ?? autoId;

  return (
    <InputContext.Provider value={{ id: inputId, error, type }}>
      <div className={`flex w-full flex-col gap-1 ${className}`} {...props}>
        {children}
      </div>
    </InputContext.Provider>
  );
}

/** 라벨 */
interface InputLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  className?: string;
}

function Label({ className, ...props }: InputLabelProps) {
  const { id } = useInputContext();
  return <label htmlFor={id} className={`typography-caption-head text-base-txt-tertiary ${className ?? ''}`} {...props} />;
}

/** Input 입력필드 */
interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  maxLength?: number;
  startNode?: ReactNode;
  endNode?: ReactNode;
  ref?: React.Ref<HTMLInputElement>;
}

function Field({ className, ref, maxLength, endNode, startNode, value, onChange, ...props }: InputFieldProps) {
  const { id, error, type } = useInputContext();
  const currentLength = value ? String(value).length : 0;
  const focusRing = error ? 'focus-within:ring-[#FF6566]' : 'focus-within:ring-[rgba(255,255,255,0.60)]';

  return (
    <div className={`${focusRing} ${type === 'round' ? 'rounded-full' : 'rounded-lg'} relative flex w-full items-center bg-[#303044] px-4 py-5 transition-all focus-within:ring-2`}>
      {startNode && <>{startNode}</>}
      <input
        id={id}
        ref={ref}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        className={`typography-body-lg w-full border-none bg-transparent text-[#fff] outline-none placeholder:text-input-txt-placeholder ${className}`}
        {...props}
      />
      {maxLength && !endNode && (
        <span className="typography-caption-body text-input-txt-placeholder">
          {currentLength}/{maxLength}
        </span>
      )}
      {endNode && <>{endNode}</>}
    </div>
  );
}

/** 에러문구 */
interface InputErrorTextProps extends HTMLAttributes<HTMLSpanElement> {
  className?: string;
}

function ErrorText({ className, children, ...props }: InputErrorTextProps) {
  const { error } = useInputContext();
  return (
    <>
      {error && (
        <span className={`typography-caption-body text-[#FF6566] ${className}`} {...props}>
          {children}
        </span>
      )}
    </>
  );
}

const Input = {
  Root,
  Label,
  Field,
  ErrorText,
};

export default Input;
